import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Stack,
  useTheme,
  IconButton,
  Typography,
  Avatar,
  Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { sendWebSocketMessage } from "../../hooks/useWebSocket";
import { useDispatch } from "react-redux";
import {
  updateLoading,
  updateLoadingMsg,
  addActiveRequests,
} from "../../Redux/Reducers/uiSlice";

const fontFamily = `'Inter', 'SF Pro Display', 'Roboto', 'Arial', sans-serif`;

const AddAccountingDialog = ({ open, onClose, accountingData, managers, funds }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const accountingId = accountingData[0]?.accountingId;
  const managerId = managers[0]?.managerId || "";

  const [formData, setFormData] = useState({
    entityType: "Fund",
    entity: "",
    entityAUM: "",
  });
  // Get entity options based on entity type
  const entityOptions = useMemo(() => {
    if (!funds || funds.length === 0) return [];

    // Filter based on entity type
    const filtered = funds?.filter((fund) => {
      if (formData.entityType === "Fund") {
        // Show funds where isSma is false or undefined
        return !fund?.isSMA;
      } else {
        // Show funds where isSma is true
        return fund?.isSMA === true;
      }
    });

    // Map to options and sort
    const options = filtered?.map((fund) => ({
      id: fund?.fundId,
      name: fund?.abbreviation || fund?.fundId,
    }));

    return options?.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [formData.entityType, funds]);

  // Reset entity when entity type changes
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      entity: "",
    }));
  }, [formData.entityType]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = () => {
    // Validate required fields
    if (!formData.entity || !formData.entityAUM) {
      alert("Please fill in all required fields");
      return;
    }

    const selectedEntity = entityOptions.find((e) => e.name === formData.entity);
    if (!selectedEntity) {
      alert("Invalid entity selected");
      return;
    }



    const requestData = {
      action: "update_accounting_aum",
      data: {
        accountingId: accountingId,
        fundAUM: parseFloat(formData.entityAUM),
        fundId: selectedEntity.id,
        isSMA: formData.entityType === "SMA",
        managerId: managerId,
      },
    };


    // Show loader and add to active requests
    dispatch(updateLoading(true));
    dispatch(updateLoadingMsg("Adding accounting data..."));
    dispatch(addActiveRequests("update_accounting_aum"));

    sendWebSocketMessage(JSON.stringify(requestData));
    handleClose();
  };

  const handleClose = () => {
    setFormData({
      entityType: "Fund",
      entity: "",
      entityAUM: "",
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 4px 24px 0 rgba(16,30,54,0.10)",
          fontFamily,
          p: 0,
        },
      }}
    >
      <Box sx={{ px: 4, pt: 3, pb: 0 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar
              sx={{
                bgcolor: theme.palette.success.main,
                width: 32,
                height: 32,
              }}
            >
              <AddIcon sx={{ color: "#fff", fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: 22,
                  fontFamily,
                  lineHeight: 1.2,
                }}
              >
                Add Accounting Item
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontFamily,
                  mt: 0.5,
                }}
              >
                Enter entity details to add to accounting.
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={handleClose} size="small" sx={{ ml: 1 }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>
      
      <DialogContent sx={{ pt: 2, pb: 2, px: 4 }}>
        <Stack spacing={2.5}>
          {/* Entity Type */}
          <Autocomplete
            options={["Fund", "SMA"]}
            value={formData.entityType}
            onChange={(event, newValue) =>
              handleInputChange("entityType", newValue || "Fund")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Entity Type *"
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                  sx: { fontWeight: 600, fontFamily },
                }}
                inputProps={{ ...params.inputProps, style: { fontFamily } }}
              />
            )}
            disableClearable
          />

          {/* Entity */}
          <Autocomplete
            options={entityOptions}
            getOptionLabel={(option) => option.name || ""}
            value={entityOptions.find((e) => e.name === formData.entity) || null}
            onChange={(event, newValue) =>
              handleInputChange("entity", newValue?.name || "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={`${formData.entityType} *`}
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                  sx: { fontWeight: 600, fontFamily },
                }}
                inputProps={{ ...params.inputProps, style: { fontFamily } }}
              />
            )}
          />

          {/* Entity AUM */}
          <TextField
            label="Entity AUM *"
            name="entityAUM"
            type="number"
            value={formData.entityAUM}
            onChange={(e) => handleInputChange("entityAUM", e.target.value)}
            fullWidth
            InputLabelProps={{
              shrink: true,
              sx: { fontWeight: 600, fontFamily },
            }}
            inputProps={{ 
              style: { fontFamily },
              step: "0.01",
            }}
            variant="outlined"
          />
        </Stack>
        
        <DialogActions sx={{ px: 0, pb: 0, pt: 3 }}>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            sx={{
              borderRadius: 1,
              fontWeight: 600,
              fontFamily,
              textTransform: "none",
              px: 3,
              minWidth: 120,
            }}
          >
            Add Item
          </Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};

export default AddAccountingDialog;

